import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { policyIcon } from "~/pages/inventory/utils/policyIcon";
import { Flex } from "~/components/Flex";
import { PolicyActionChip } from "~/components/PolicyActionChip";
import { AccessTime as AccessTimeIcon } from "@mui/icons-material";
import { FormatDate, isWithinInterval, subDays } from "~/lib/date";
import { CompactRiskScoreBox } from "~/components/CompactRiskScoreBox/CompactRiskScoreBox";
import { AssetResultsCell } from "~/pages/security/policies/AssetResultsCell";
import { PolicyAction } from "~/operations";
import { ActivePolicyNode } from "~/pages/security/policies/types";
import { SwitchBaseProps } from "@mui/material/internal/SwitchBase";
import { ClampWithTooltip } from "~/components/ClampWithTooltip/ClampWithTooltip";
import { FleetIcon, VersionsIcon } from "~/icons";
import { ContentFlag } from "~/components/ui-library";
import { pluralize } from "~/lib/pluralize";
import { isPolicyCustom } from "~/lib/mrn";

type PolicyType = {
  policy: ActivePolicyNode;
  canEdit?: boolean;
  onClick?: React.MouseEventHandler<HTMLTableRowElement> | undefined;
  isSelected?: boolean;
  onCheckboxSelect?: SwitchBaseProps["onChange"];
};

export const PolicyListRow = ({
  policy,
  canEdit = false,
  onClick,
  isSelected = false,
  onCheckboxSelect,
}: PolicyType) => {
  const { action, score, mrn, name, updatedAt, lastAppliedAt, version } =
    policy;

  const inPreviewMode = action === PolicyAction.Ignore;
  const isNew = lastAppliedAt
    ? isWithinInterval(new Date(lastAppliedAt), {
        start: subDays(new Date(), 7),
        end: new Date(),
      })
    : false;
  const isCustom = isPolicyCustom(mrn);
  const className = isSelected ? "selected" : "";

  return (
    <TableRow
      key={mrn}
      onClick={onClick}
      className={className + " " + "policy-row"}
    >
      {canEdit && (
        <TableCell>
          <FormControlLabel
            control={
              <Checkbox checked={isSelected} onChange={onCheckboxSelect} />
            }
            label=""
            onClick={(e) => e.stopPropagation()}
          />
        </TableCell>
      )}
      <TableCell>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            paddingLeft: canEdit ? 2 : 1,
          }}
        >
          {policyIcon(name, "large")}
        </Box>
      </TableCell>
      <TableCell sx={{ maxWidth: "1px !important", width: "100%" }}>
        <Box>
          <Flex alignItems="center" gap={1} mb={1}>
            <ClampWithTooltip
              id="policy-name"
              sx={{ fontWeight: 700, cursor: "pointer" }}
            >
              {name}
            </ClampWithTooltip>
            {isNew && (
              <Typography
                className="new-badge"
                variant="caption"
                sx={{
                  color: "warning.main",
                  fontWeight: 600,
                  verticalAlign: "middle",
                }}
              >
                NEW
              </Typography>
            )}
            {isCustom && (
              <ContentFlag
                flag="custom"
                layout={{
                  color: (theme) => theme.palette.secondary.light,
                  borderColor: (theme) => theme.palette.secondary.main,
                }}
              />
            )}
          </Flex>

          <Grid container sx={{ columnGap: 2, rowGap: 0.5 }} size={12}>
            {inPreviewMode && (
              <Grid
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
                size={{
                  xs: 12,
                  sm: "auto",
                }}
              >
                <PolicyActionChip action={action} />
              </Grid>
            )}
            <Grid
              sx={{
                display: "flex",
                alignItems: "center",
              }}
              size={{
                xs: 12,
                sm: "auto",
              }}
            >
              <Typography
                className="policy-asset-count"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: (theme) => theme.spacing(1.5),
                  color: "text.secondary",
                }}
              >
                <FleetIcon fontSize="small" sx={{ mr: 1 }} />
                {score?.blastRadius?.assets ?? 0}{" "}
                {pluralize("Asset", score?.blastRadius?.assets ?? 0)}
              </Typography>
            </Grid>
            <Grid
              sx={{
                display: "flex",
                alignItems: "center",
              }}
              size={{
                xs: 12,
                sm: "auto",
              }}
            >
              <Typography
                className="policy-last-updated"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: (theme) => theme.spacing(1.5),
                  color: "text.secondary",
                }}
              >
                <AccessTimeIcon fontSize="small" sx={{ mr: 1 }} />
                Last update: {FormatDate(updatedAt)}
              </Typography>
            </Grid>
            {version && (
              <Grid
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
                size={{
                  xs: 12,
                  sm: "auto",
                }}
              >
                <Typography
                  className="policy-last-updated"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: (theme) => theme.spacing(1.5),
                    color: "text.secondary",
                  }}
                >
                  <VersionsIcon fontSize="small" sx={{ mr: 1 }} />
                  Version: {version}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      </TableCell>
      <TableCell>
        {score?.riskValue !== undefined && score.riskValue !== null && (
          <CompactRiskScoreBox
            inPreviewMode={inPreviewMode}
            riskValue={score.riskValue}
            riskRating={score.rating}
          />
        )}
      </TableCell>
      <TableCell sx={{ paddingRight: canEdit ? 0 : 9 }}>
        <AssetResultsCell
          blastRadius={score?.blastRadius}
          inPreviewMode={inPreviewMode}
        />
      </TableCell>
    </TableRow>
  );
};
